import { ComponentBase } from "../../core/ComponentBase.js";

export class Timeline extends ComponentBase {
    static styles = `
    .timeline-wrapper {
        position: relative;
        width: 100%;
        height: 200px;
        background-color: var(--bs-tertiary-bg);
        border-radius: var(--bs-border-radius);
        padding: 0.5rem;
        overflow: hidden;
        display: flex;
        flex-direction: column;
    }
    
    /* --- Integrated Controls Bar --- */
    .timeline-controls-bar {
        flex-shrink: 0;
        height: 40px;
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 0 0.5rem;
        border-bottom: 1px solid var(--bs-border-color);
        background-color: var(--bs-body-bg);
        gap: 1rem;
    }
    
    .timeline-controls-left {
        display: flex;
        align-items: center;
        gap: 0.75rem;
    }
    
    .timeline-play-pause-btn {
        font-size: 1.25rem;
        width: 32px;
        height: 32px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        border: none;
        cursor: pointer;
        transition: all 0.2s ease;
    }
    .timeline-play-pause-btn:hover {
        transform: scale(1.05);
    }
    
    .timeline-time-display {
        font-family: 'Courier New', monospace;
        font-size: 0.9rem;
        font-weight: 500;
        color: var(--bs-body-color);
        min-width: 100px;
        text-align: center;
    }
    
    .timeline-controls-right {
        display: flex;
        align-items: center;
        gap: 0.5rem;
    }
    
    .timeline-clip-actions {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        padding-left: 0.75rem;
        border-left: 1px solid var(--bs-border-color-translucent);
    }
    
    .timeline-menu-btn {
        position: relative;
    }
    
    .timeline-dropdown-menu {
        position: absolute;
        top: 100%;
        right: 0;
        margin-top: 0.25rem;
        background-color: var(--bs-body-bg);
        border: 1px solid var(--bs-border-color);
        border-radius: var(--bs-border-radius);
        box-shadow: 0 4px 12px rgba(0,0,0,0.15);
        min-width: 220px;
        z-index: 1000;
        display: none;
        max-height: 200px;
        overflow-y: auto;
    }
    
    .timeline-dropdown-menu.show {
        display: block;
    }
    
    .timeline-dropdown-item {
        padding: 0.5rem 1rem;
        cursor: pointer;
        transition: background-color 0.15s ease;
        display: flex;
        align-items: center;
        gap: 0.5rem;
        font-size: 0.875rem;
        border: none;
        background: none;
        width: 100%;
        text-align: left;
        color: var(--bs-body-color);
    }
    
    .timeline-dropdown-item:hover {
        background-color: var(--bs-tertiary-bg);
    }
    
    .timeline-dropdown-item:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
    
    .timeline-dropdown-item:disabled:hover {
        background-color: transparent;
    }
    
    .timeline-dropdown-divider {
        height: 1px;
        background-color: var(--bs-border-color);
        margin: 0.25rem 0;
    }
    
    /* --- Fixed Ruler Styles --- */
    .timeline-ruler-container {
        flex-shrink: 0;
        height: 24px;
        border-bottom: 1px solid var(--bs-border-color);
        background-color: var(--bs-tertiary-bg);
        position: relative;
        z-index: 15;
        overflow: hidden;
    }
    
    .timeline-ruler-scroll {
        overflow: hidden;
        position: relative;
        width: 100%;
        height: 100%;
    }
    
    .timeline-ruler {
        position: absolute;
        height: 100%;
        display: flex;
        align-items: flex-end;
        top: 0;
        left: 0;
        will-change: transform;
    }
    
    .ruler-marker {
        position: absolute;
        bottom: 0;
        height: 6px;
        width: 1px;
        background-color: var(--bs-secondary-color);
    }
    .ruler-marker.major {
        height: 12px;
    }
    .ruler-marker span {
        position: absolute;
        bottom: 12px;
        left: 0;
        transform: translateX(-50%);
        font-size: 0.65rem;
        font-weight: 500;
        color: var(--bs-secondary-color);
        user-select: none;
        white-space: nowrap;
        pointer-events: none;
    }
    
    .timeline-content {
        flex-grow: 1;
        overflow: hidden;
        position: relative;
    }
    
    .timeline-scroll-container {
        overflow-x: auto;
        overflow-y: auto;
        scrollbar-width: none;
        position: relative;
        height: 100%;
    }
    .timeline-scroll-container::-webkit-scrollbar {
        display: none;
    }

    .timeline-tracks-wrapper {
        position: relative;
        display: flex;
        flex-direction: column;
    }
    
    .timeline-track-group {
        display: flex;
        flex-direction: column;
        position: relative;
        background: linear-gradient(180deg, rgba(255,255,255,0.02) 0%, rgba(0,0,0,0.01) 100%);
    }

    .timeline-track {
        display: flex;
        align-items: flex-start;
        position: relative;
        min-height: 30px;
        border-bottom: 1px solid var(--bs-border-color-translucent);
    }
    .timeline-track.visual {
        min-height: 45px;
        background: linear-gradient(180deg, rgba(255,255,255,0.04) 0%, rgba(0,0,0,0.02) 100%);
    }
    .timeline-track:last-child {
        border-bottom: none;
    }
    
    /* --- Clip Interaction Styles --- */
    .timeline-clip {
        cursor: grab;
        position: absolute;
        user-select: none;
        flex-shrink: 0;
    }
    .timeline-clip.dragging {
        cursor: grabbing;
        opacity: 0.7;
        z-index: 100 !important;
    }
    .resize-handle {
        position: absolute;
        top: 0;
        bottom: 0;
        width: 8px;
        cursor: ew-resize;
        z-index: 5;
    }
    .resize-handle-left { left: 0; }
    .resize-handle-right { right: 0; }

    /* Visual clips */
    .timeline-clip.visual {
        height: 40px;
        border: 1px solid var(--bs-border-color);
        border-radius: 4px;
        overflow: hidden;
        background-color: var(--bs-secondary-bg);
        display: flex;
        align-items: center;
        justify-content: center;
        margin-top: 2px;
        box-shadow: 0 1px 3px rgba(0,0,0,0.1);
        transition: box-shadow 0.2s ease, border-color 0.2s ease, transform 0.2s ease;
    }
    .timeline-clip.visual:hover {
        box-shadow: 0 2px 6px rgba(0,0,0,0.15);
        border-color: var(--bs-primary);
    }
    
    .timeline-clip.visual.selected {
        border-color: var(--bs-primary);
        box-shadow: 0 0 0 2px var(--bs-primary-border-subtle), 0 0 12px rgba(var(--bs-primary-rgb), 0.5);
        z-index: 50;
    }
    
    .timeline-clip.visual img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        pointer-events: none;
    }
    
    /* Audio & Text clips */
    .timeline-clip.dialogue,
    .timeline-clip.sfx,
    .timeline-clip.bgMusic,
    .timeline-clip.ambient,
    .timeline-clip.layer {
        height: 12px;
        border-radius: 2px;
        margin-top: 9px;
        opacity: 0.9;
        min-width: 20px;
        z-index: 1;
        display: flex;
        align-items: center;
        box-shadow: 0 1px 2px rgba(0,0,0,0.2);
        transition: all 0.2s ease;
        padding: 0 0.4rem;
        overflow: hidden;
    }
    .timeline-clip.dialogue:hover,
    .timeline-clip.sfx:hover,
    .timeline-clip.bgMusic:hover,
    .timeline-clip.ambient:hover,
    .timeline-clip.layer:hover {
        height: 14px;
        opacity: 1;
        z-index: 2;
        margin-top: 8px;
        box-shadow: 0 2px 4px rgba(0,0,0,0.3);
    }
    
    .timeline-clip.dialogue.selected,
    .timeline-clip.sfx.selected,
    .timeline-clip.bgMusic.selected,
    .timeline-clip.ambient.selected,
    .timeline-clip.layer.selected {
        height: 16px;
        margin-top: 7px;
        z-index: 50;
        box-shadow: 0 0 0 2px var(--bs-primary-border-subtle), 0 0 8px rgba(var(--bs-primary-rgb), 0.6);
    }
    
    /* --- Clip Content Styles --- */
    .clip-icon {
        flex-shrink: 0;
        margin-right: 0.3rem;
        color: rgba(255,255,255,0.8);
        font-size: 0.7rem;
    }
    .clip-label {
        font-size: 0.65rem;
        font-weight: 500;
        color: rgba(255,255,255,0.9);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }
    .timeline-clip.visual .clip-icon {
        position: absolute;
        top: 4px;
        left: 4px;
        z-index: 2;
        color: white;
        text-shadow: 0 1px 3px rgba(0,0,0,0.6);
        font-size: 0.9rem;
        margin-right: 0;
    }
    
    /* Audio track colors */
    .timeline-clip.dialogue { background-color: #3b82f6; }
    .timeline-clip.sfx { background-color: #8b5cf6; }
    .timeline-clip.bgMusic { background-color: #ec4899; }
    .timeline-clip.ambient { background-color: #10b981; }
    .timeline-clip.layer { background-color: #f59e0b; }
    
    /* --- Styles for Dirty and Missing Assets --- */
    .timeline-clip.is-dirty::after {
        content: '';
        position: absolute;
        top: 3px;
        right: 3px;
        width: 8px;
        height: 8px;
        background-color: var(--bs-orange);
        border-radius: 50%;
        border: 1px solid rgba(255,255,255,0.8);
        box-shadow: 0 0 4px var(--bs-orange);
        z-index: 6;
    }
    
    .timeline-clip.visual.missing-asset {
        border: 1px dashed var(--bs-warning);
        background-color: var(--bs-warning-bg-subtle);
    }
    
    /* --- FIX: Exclude .layer from this rule --- */
    .timeline-clip.dialogue.missing-asset,
    .timeline-clip.sfx.missing-asset,
    .timeline-clip.bgMusic.missing-asset,
    .timeline-clip.ambient.missing-asset {
        background-image: repeating-linear-gradient(
            45deg,
            rgba(0, 0, 0, 0.15),
            rgba(0, 0, 0, 0.15) 10px,
            rgba(0, 0, 0, 0.25) 10px,
            rgba(0, 0, 0, 0.25) 20px
        );
    }

    /* --- Styles for Duration Mismatch --- */
    .timeline-clip.extended-duration {
        background-color: var(--bs-danger-bg-subtle);
        border: 1px dashed var(--bs-danger);
    }
    .video-duration-indicator {
        position: absolute;
        left: 0;
        top: 0;
        height: 100%;
        overflow: hidden;
        border-right: 2px solid var(--bs-danger);
    }
    .video-duration-indicator img {
        width: auto;
        height: 100%;
        object-fit: cover;
        pointer-events: none;
    }
    .audio-duration-indicator {
        position: absolute;
        left: 0;
        top: 0;
        height: 100%;
        border-right: 2px solid var(--bs-danger);
        border-radius: 2px 0 0 2px;
    }
    .dialogue .audio-duration-indicator { background-color: #3b82f6; }
    .sfx .audio-duration-indicator { background-color: #8b5cf6; }
    .bgMusic .audio-duration-indicator { background-color: #ec4899; }
    .ambient .audio-duration-indicator { background-color: #10b981; }
    /* Layer clips don't have this, but it's harmless to leave */
    .layer .audio-duration-indicator { background-color: #f59e0b; }

    @media (min-width: 768px) {
        .timeline-wrapper {
            height: 100%;
        }
    }

    .timeline-playhead {
        position: absolute;
        top: 0;
        width: 2px;
        height: 100%;
        background: linear-gradient(90deg, #dc2626 0%, #ef4444 50%, #dc2626 100%);
        cursor: ew-resize;
        z-index: 25;
        pointer-events: all;
        box-shadow: 0 0 4px rgba(220, 38, 38, 0.5);
    }
    .timeline-playhead::before {
        content: '';
        position: absolute;
        top: -24px;
        left: -4px;
        width: 10px;
        height: 10px;
        background-color: #dc2626;
        border-radius: 50%;
        box-shadow: 0 1px 3px rgba(0,0,0,0.3);
    }
    
    .add-clip-btn-timeline {
        position: absolute;
        right: 10px;
        top: 10px;
        height: 36px;
        width: 36px;
        border: 2px dashed var(--bs-border-color);
        border-radius: var(--bs-border-radius);
        background-color: transparent;
        color: var(--bs-secondary-color);
        font-size: 1.2rem;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 30;
        transition: all 0.2s ease;
    }
    .add-clip-btn-timeline:hover {
        background-color: var(--bs-primary-bg-subtle);
        color: var(--bs-primary);
        border-color: var(--bs-primary);
        box-shadow: 0 2px 6px rgba(0,0,0,0.1);
    }
    
    .timeline-empty-state {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        text-align: center;
        color: var(--bs-secondary-color);
        z-index: 5;
    }
    .track-settings-popover .popover-body {
        padding: 1rem;
        min-width: 280px;
    }
    .track-setting-row {
        display: flex;
        align-items: center;
        gap: 0.75rem;
        margin-bottom: 0.75rem;
    }
    .track-setting-row label {
        flex-basis: 80px;
        font-size: 0.8rem;
    }
    .track-setting-row input[type="range"] {
        flex-grow: 1;
    }
`;



    constructor(config) {
        super(config);
        this.clips = [];
        this.projectSettings = {};
        this.totalDuration = 0;
        this.pixelsPerSecond = 50;
        this.selectedClipId = null;
        this.trackOrder = ['visual', 'dialogue', 'sfx', 'bgMusic', 'ambient', 'layer'];
        this.trackIcons = {
            visual: 'bi-image',
            visual_video: 'bi-film',
            visual_missing: 'bi-image-alt',
            dialogue: 'bi-chat-dots',
            sfx: 'bi-speaker-fill',
            bgMusic: 'bi-music-note-beamed',
            ambient: 'bi-wind',
            layer: 'bi-type'
        };

        this.isPlaying = false;
        this.currentTime = 0;
        this.isRippleEditEnabled = true;
        this.trackSettingsPopover = null;

        this.dragState = {
            active: false,
            type: null,
            clipId: null,
            clipElement: null,
            initialX: 0,
            initialLeft: 0,
            initialWidth: 0,
            hasMoved: false,
        };

        this.applyStyles();
    }

    async initialize() {
        this.container = document.createElement('div');
        this.container.className = 'timeline-wrapper';
        this.render();
        await super.initialize();
    }

    render() {
        this.container.innerHTML = `
            <div class="timeline-controls-bar">
                <div class="timeline-controls-left">
                    <!-- START OF FIX: Add tabindex for focus trigger -->
                    <button class="btn btn-sm btn-outline-secondary" id="track-settings-btn" title="Track Settings" tabindex="0">
                        <i class="bi bi-sliders"></i>
                    </button>
                    <!-- END OF FIX -->
                    <button class="btn btn-primary timeline-play-pause-btn">
                        <i class="bi ${this.isPlaying ? 'bi-pause-fill' : 'bi-play-fill'}"></i>
                    </button>
                    <div class="timeline-time-display">
                        <span class="current-time">${this.formatTime(this.currentTime)}</span> / <span class="total-duration">${this.formatTime(this.totalDuration)}</span>
                    </div>
                </div>
                <div class="timeline-controls-right">
                    <button class="btn btn-sm btn-outline-secondary" id="ripple-edit-toggle" title="Toggle Ripple Edit (shifts subsequent clips)">
                        <i class="bi bi-link-45deg"></i>
                    </button>
                    <div id="timeline-clip-actions"></div>
                    <div class="timeline-menu-btn">
                        <button class="btn btn-sm btn-outline-secondary" id="timeline-menu-toggle">
                            <i class="bi bi-three-dots-vertical"></i>
                        </button>
                        <div class="timeline-dropdown-menu" id="timeline-dropdown-menu"></div>
                    </div>
                </div>
            </div>
            <div class="timeline-ruler-container">
                <div class="timeline-ruler-scroll">
                    <div class="timeline-ruler"></div>
                </div>
            </div>
            <div class="timeline-content">
                <div class="timeline-scroll-container">
                    <div class="timeline-tracks-wrapper">
                        <div class="timeline-playhead"></div>
                    </div>
                </div>
            </div>
        `;

        this.playPauseBtn = this.container.querySelector('.timeline-play-pause-btn');
        this.currentTimeEl = this.container.querySelector('.current-time');
        this.totalDurationEl = this.container.querySelector('.total-duration');
        this.clipActionsContainer = this.container.querySelector('#timeline-clip-actions');
        this.menuToggleBtn = this.container.querySelector('#timeline-menu-toggle');
        this.dropdownMenu = this.container.querySelector('#timeline-dropdown-menu');
        this.scrollContainer = this.container.querySelector('.timeline-scroll-container');
        this.rulerScroll = this.container.querySelector('.timeline-ruler-scroll');
        this.ruler = this.container.querySelector('.timeline-ruler');
        this.tracksWrapper = this.container.querySelector('.timeline-tracks-wrapper');
        this.playhead = this.container.querySelector('.timeline-playhead');
        this.trackSettingsBtn = this.container.querySelector('#track-settings-btn');
        this.rippleEditToggleBtn = this.container.querySelector('#ripple-edit-toggle');

        this.updateRippleButton();
        this.renderRuler();
        this.renderTracks();
        this.renderClipActions();
        this.renderDropdownMenu();
        this.attachEventListeners();

        return this.container;
    }

    setData(clips, selectedClipId, projectSettings) {
        const scrollLeft = this.scrollContainer ? this.scrollContainer.scrollLeft : 0;
        const scrollTop = this.scrollContainer ? this.scrollContainer.scrollTop : 0;

        this.clips = clips || [];
        this.selectedClipId = selectedClipId;
        this.projectSettings = projectSettings || {};
        this.totalDuration = this.clips.reduce((sum, clip) =>
            Math.max(sum, clip.startTime + clip.duration), 0) || 30;

        if (!this.tracksWrapper) return;

        this.renderTracks();
        this.renderRuler();
        this.renderClipActions();
        this.updateTimeDisplay();

        if (this.scrollContainer) {
            this.scrollContainer.scrollLeft = scrollLeft;
            this.scrollContainer.scrollTop = scrollTop;
            this.syncScroll();
        }
    }

    renderClipActions() {
        if (!this.clipActionsContainer) return;
        this.clipActionsContainer.innerHTML = '';

        const selectedClip = this.clips.find(c => c._id === this.selectedClipId);
        if (!selectedClip) return;

        const actionsDiv = document.createElement('div');
        actionsDiv.className = 'timeline-clip-actions';

        const hasAsset = !!selectedClip.assetPath;
        const isAudioClip = ['dialogue', 'sfx', 'bgMusic', 'ambient'].includes(selectedClip.type);
        const isVisualWithAsset = selectedClip.type === 'visual' && hasAsset;
        const hasVideo = !!selectedClip.videoAssetPath;

        const generateBtnIcon = hasAsset ? 'bi-arrow-repeat' : 'bi-stars';
        const generateBtnTitle = hasAsset ? 'Regenerate Media' : 'Generate Media';

        const previewBtnHtml = hasAsset ? `
            <button class="btn btn-sm btn-outline-secondary" data-action="preview" title="Preview Asset">
                <i class="bi bi-play-circle-fill"></i>
            </button>
        ` : '';

        // --- FIX: Renamed and updated logic for resizing ---
        const resizeBtnHtml = (isAudioClip && hasAsset && selectedClip.actualDuration) ? `
            <button class="btn btn-sm btn-outline-secondary" data-action="resize-to-media" title="Resize clip to media length">
                <i class="bi bi-arrows-angle-expand"></i>
            </button>
        ` : (isVisualWithAsset && hasVideo && selectedClip.actualDuration) ? `
            <button class="btn btn-sm btn-outline-secondary" data-action="resize-to-media" title="Extend clip to full video length">
                <i class="bi bi-arrows-angle-expand"></i>
            </button>
        ` : '';

        const animationBtnHtml = isVisualWithAsset ? `
            <button class="btn btn-sm btn-outline-success" data-action="generate-animation" title="Generate Video Animation">
                <i class="bi bi-film"></i>
            </button>
        ` : '';

        actionsDiv.innerHTML = `
            ${previewBtnHtml}
            <button class="btn btn-sm btn-outline-primary" data-action="edit" title="Edit Clip">
                <i class="bi bi-pencil-square"></i>
            </button>
            <button class="btn btn-sm btn-outline-info" data-action="generate" title="${generateBtnTitle}">
                <i class="bi ${generateBtnIcon}"></i>
            </button>
            ${animationBtnHtml}
            ${resizeBtnHtml}
            <button class="btn btn-sm btn-outline-danger" data-action="delete" title="Delete Clip">
                <i class="bi bi-trash"></i>
            </button>
        `;

        if (hasAsset) {
            actionsDiv.querySelector('[data-action="preview"]')?.addEventListener('click', () => {
                this.emit('previewClip', { clip: selectedClip });
            });
        }

        // --- FIX: Attach event listener for the unified resize button ---
        if (actionsDiv.querySelector('[data-action="resize-to-media"]')) {
            actionsDiv.querySelector('[data-action="resize-to-media"]').addEventListener('click', () => {
                this.emit('resizeClipToMedia', { clipId: selectedClip._id });
            });
        }

        if (isVisualWithAsset) {
            actionsDiv.querySelector('[data-action="generate-animation"]')?.addEventListener('click', (e) => {
                this.emit('generateVideoAnimation', { clipId: selectedClip._id, force: e.altKey });
            });
        }

        actionsDiv.querySelector('[data-action="edit"]').addEventListener('click', () => {
            this.emit('editClip', { clipId: selectedClip._id });
        });
        actionsDiv.querySelector('[data-action="generate"]').addEventListener('click', () => {
            this.emit('generateClipAsset', { clip: selectedClip, force: hasAsset });
        });
        actionsDiv.querySelector('[data-action="delete"]').addEventListener('click', () => {
            this.emit('deleteClip', { clipId: selectedClip._id });
        });

        this.clipActionsContainer.appendChild(actionsDiv);
    }

    renderDropdownMenu() {
        if (!this.dropdownMenu) return;

        this.dropdownMenu.innerHTML = `
            <button class="timeline-dropdown-item" data-action="edit-story">
                <i class="bi bi-book-fill"></i>
                <span>View/Edit Story</span>
            </button>
            <button class="timeline-dropdown-item" data-action="edit-project-settings">
                <i class="bi bi-gear-fill"></i>
                <span>Project Settings</span>
            </button>
            <div class="timeline-dropdown-divider"></div>
            <button class="timeline-dropdown-item" data-action="generate-all-assets">
                <i class="bi bi-stars"></i>
                <span>Generate All Assets</span>
            </button>
            <button class="timeline-dropdown-item" data-action="generate-animations">
                <i class="bi bi-film"></i>
                <span>Generate Animations</span>
            </button>
            <button class="timeline-dropdown-item" data-action="ai-modify-project">
                <i class="bi bi-magic"></i>
                <span>AI Editor</span>
            </button>
            <div class="timeline-dropdown-divider"></div>
            <!-- START OF FIX: New Timeline Tools Menu Item -->
            <button class="timeline-dropdown-item" data-action="timeline-tools">
                <i class="bi bi-tools"></i>
                <span>Timeline Tools</span>
            </button>
            <!-- END OF FIX -->
            <button class="timeline-dropdown-item" data-action="update-all-clip-durations">
                <i class="bi bi-stopwatch"></i>
                <span>Update Clip Durations</span>
            </button>
            <button class="timeline-dropdown-item" data-action="generate-video">
                <i class="bi bi-camera-reels-fill"></i>
                <span>Render Final Video</span>
            </button>
        `;

        this.dropdownMenu.querySelector('[data-action="edit-story"]').addEventListener('click', () => {
            this.emit('editStory');
            this.hideDropdownMenu();
        });
        this.dropdownMenu.querySelector('[data-action="edit-project-settings"]').addEventListener('click', () => {
            this.emit('editProjectSettings');
            this.hideDropdownMenu();
        });
        this.dropdownMenu.querySelector('[data-action="generate-all-assets"]').addEventListener('click', () => {
            this.emit('generateAllAssets');
            this.hideDropdownMenu();
        });
        this.dropdownMenu.querySelector('[data-action="generate-animations"]').addEventListener('click', () => {
            this.emit('generateAnimations');
            this.hideDropdownMenu();
        });
        this.dropdownMenu.querySelector('[data-action="ai-modify-project"]').addEventListener('click', () => {
            this.emit('aiModifyProject');
            this.hideDropdownMenu();
        });
        // --- START OF FIX: Emit event for Timeline Tools ---
        this.dropdownMenu.querySelector('[data-action="timeline-tools"]').addEventListener('click', () => {
            this.emit('showTimelineTools');
            this.hideDropdownMenu();
        });
        // --- END OF FIX ---
        this.dropdownMenu.querySelector('[data-action="update-all-clip-durations"]').addEventListener('click', () => {
            this.emit('updateAllClipDurations');
            this.hideDropdownMenu();
        });
        this.dropdownMenu.querySelector('[data-action="generate-video"]').addEventListener('click', () => {
            this.emit('generateVideo');
            this.hideDropdownMenu();
        });
    }



    renderRuler() {
        if (!this.ruler) return;

        this.ruler.innerHTML = '';

        const trackWidth = Math.max(this.totalDuration * this.pixelsPerSecond + 100, this.scrollContainer.clientWidth);
        this.ruler.style.width = `${trackWidth}px`;

        const majorInterval = 10;
        const minorInterval = 1;
        for (let t = 0; t <= this.totalDuration; t += minorInterval) {
            const isMajor = t % majorInterval === 0;
            const marker = document.createElement('div');
            marker.className = `ruler-marker ${isMajor ? 'major' : 'minor'}`;
            marker.style.left = `${t * this.pixelsPerSecond}px`;
            if (isMajor) {
                marker.innerHTML = `<span>${t}s</span>`;
            }
            this.ruler.appendChild(marker);
        }
    }

    renderTracks() {
        if (!this.tracksWrapper) return;

        this.tracksWrapper.innerHTML = '<div class="timeline-playhead"></div>';
        this.playhead = this.tracksWrapper.querySelector('.timeline-playhead');

        const trackWidth = Math.max(this.totalDuration * this.pixelsPerSecond + 100, this.scrollContainer.clientWidth);
        this.tracksWrapper.style.width = `${trackWidth}px`;

        const trackGroups = {};
        this.trackOrder.forEach(trackType => {
            trackGroups[trackType] = { clips: [], rows: [] };
        });

        this.clips.forEach(clip => {
            if (trackGroups[clip.type]) {
                trackGroups[clip.type].clips.push(clip);
            }
        });

        Object.keys(trackGroups).forEach(trackType => {
            const clipsForTrack = trackGroups[trackType].clips;
            const rows = [];

            clipsForTrack.forEach(clip => {
                let placed = false;
                for (let i = 0; i < rows.length; i++) {
                    const row = rows[i];
                    const overlaps = row.some(existingClip =>
                        this.clipsOverlap(clip, existingClip)
                    );
                    if (!overlaps) {
                        row.push(clip);
                        placed = true;
                        break;
                    }
                }
                if (!placed) {
                    rows.push([clip]);
                }
            });

            trackGroups[trackType].rows = rows;
        });

        this.trackOrder.forEach(trackType => {
            const trackGroupData = trackGroups[trackType];
            if (trackGroupData.rows.length === 0) return;

            const groupEl = document.createElement('div');
            groupEl.className = 'timeline-track-group';
            groupEl.dataset.trackType = trackType;

            trackGroupData.rows.forEach((clipsInRow, rowIndex) => {
                const trackEl = document.createElement('div');
                trackEl.className = `timeline-track ${trackType === 'visual' ? 'visual' : ''}`;
                trackEl.dataset.trackType = trackType;
                trackEl.dataset.rowIndex = rowIndex;

                clipsInRow.forEach(clip => {
                    const clipEl = document.createElement('div');
                    clipEl.className = `timeline-clip ${clip.type}`;
                    clipEl.dataset.clipId = clip._id;
                    clipEl.style.left = `${clip.startTime * this.pixelsPerSecond}px`;
                    clipEl.style.width = `${clip.duration * this.pixelsPerSecond}px`;

                    if (clip._id === this.selectedClipId) clipEl.classList.add('selected');
                    if (clip.isDirty) clipEl.classList.add('is-dirty');

                    let iconClass = this.trackIcons[clip.type] || 'bi-question-circle';
                    if (clip.type === 'visual') {
                        if (clip.videoAssetPath) iconClass = this.trackIcons.visual_video;
                        else if (clip.assetPath) iconClass = this.trackIcons.visual;
                        else iconClass = this.trackIcons.visual_missing;
                    }

                    clipEl.innerHTML = `
                    <i class="bi ${iconClass} clip-icon"></i>
                    <div class="resize-handle resize-handle-left"></div>
                    <div class="resize-handle resize-handle-right"></div>
                    ${clip.type !== 'visual' ? `<span class="clip-label">${this.t(clip.type)}</span>` : ''}
                `;

                    const DURATION_TOLERANCE = 0.1;
                    const hasMismatch = clip.actualDuration && clip.duration > clip.actualDuration + DURATION_TOLERANCE;

                    if (clip.type === 'visual') {
                        clipEl.classList.add('visual');
                        if (hasMismatch && clip.videoAssetPath) {
                            clipEl.classList.add('extended-duration');
                            const videoPortionPercent = (clip.actualDuration / clip.duration) * 100;
                            const indicator = document.createElement('div');
                            indicator.className = 'video-duration-indicator';
                            indicator.style.width = `${videoPortionPercent}%`;
                            const cacheBuster = clip.assetHash || Date.now();
                            const imgSrc = `/${clip.assetPath}?v=${cacheBuster}`;
                            const imgEl = document.createElement('img');
                            imgEl.src = imgSrc;
                            imgEl.alt = "Clip thumbnail";
                            imgEl.style.width = `${parseFloat(clipEl.style.width)}px`;
                            indicator.appendChild(imgEl);
                            clipEl.appendChild(indicator);
                        } else if (clip.assetPath) {
                            const cacheBuster = clip.assetHash || Date.now();
                            const imgSrc = `/${clip.assetPath}?v=${cacheBuster}`;
                            const imgEl = document.createElement('img');
                            imgEl.src = imgSrc;
                            imgEl.alt = "Clip thumbnail";
                            clipEl.prepend(imgEl);
                        } else {
                            clipEl.classList.add('missing-asset');
                        }
                    } else { // Non-visual clips
                        // --- FIX: Exclude 'layer' type from asset and duration checks ---
                        if (clip.type !== 'layer') {
                            if (!clip.assetPath) {
                                clipEl.classList.add('missing-asset');
                            }
                            if (hasMismatch) {
                                clipEl.classList.add('extended-duration');
                                const audioPortionPercent = (clip.actualDuration / clip.duration) * 100;
                                const indicator = document.createElement('div');
                                indicator.className = 'audio-duration-indicator';
                                indicator.style.width = `${audioPortionPercent}%`;
                                clipEl.prepend(indicator);
                            }
                        }
                    }

                    trackEl.appendChild(clipEl);
                });

                groupEl.appendChild(trackEl);
            });

            this.tracksWrapper.appendChild(groupEl);
        });

        const addBtn = document.createElement('button');
        addBtn.className = 'add-clip-btn-timeline';
        addBtn.innerHTML = '<i class="bi bi-plus-lg"></i>';
        addBtn.title = 'Add new clip';
        this.tracksWrapper.appendChild(addBtn);

        if (this.clips.length === 0) {
            const emptyState = document.createElement('div');
            emptyState.className = 'timeline-empty-state';
            emptyState.innerHTML = `
            <i class="bi bi-film" style="font-size: 2rem;"></i>
            <p>No clips yet. Click the + button to add clips.</p>
        `;
            this.tracksWrapper.appendChild(emptyState);
        }
    }


    attachEventListeners() {
        if (!this.scrollContainer || !this.tracksWrapper) return;

        this.playPauseBtn?.addEventListener('click', () => this.togglePlayPause());
        this.menuToggleBtn?.addEventListener('click', (e) => {
            e.stopPropagation();
            this.toggleDropdownMenu();
        });
        document.addEventListener('click', (e) => {
            if (!this.container.contains(e.target)) this.hideDropdownMenu();
        });

        this.rippleEditToggleBtn?.addEventListener('click', () => {
            this.isRippleEditEnabled = !this.isRippleEditEnabled;
            this.updateRippleButton();
        });

        this.trackSettingsBtn?.addEventListener('click', () => {
            this.emit('trackSettingsClicked');
        });

        const onMouseDown = (e) => {
            const target = e.target;
            const clipEl = target.closest('.timeline-clip');
            const rulerContainer = target.closest('.timeline-ruler-container');

            if (target.closest('.add-clip-btn-timeline')) {
                this.emit('addClipClicked');
                return;
            }

            if (rulerContainer || target === this.playhead || target.parentElement === this.playhead) {
                this.dragState.active = true;
                this.dragState.type = 'playhead';
                this.dragState.initialX = e.clientX;
                this.dragState.hasMoved = false;
                e.preventDefault();

                if (rulerContainer) {
                    const rect = rulerContainer.getBoundingClientRect();
                    const x = e.clientX - rect.left + this.scrollContainer.scrollLeft;
                    const time = Math.max(0, x / this.pixelsPerSecond);
                    this.emit('seek', Math.min(time, this.totalDuration));
                }

            } else if (clipEl) {
                e.preventDefault();
                e.stopPropagation();

                this.dragState.active = true;
                this.dragState.clipId = clipEl.dataset.clipId;
                this.dragState.clipElement = clipEl;
                this.dragState.initialX = e.clientX;
                this.dragState.initialLeft = clipEl.offsetLeft;
                this.dragState.initialWidth = clipEl.offsetWidth;
                this.dragState.hasMoved = false;

                if (target.classList.contains('resize-handle-left')) {
                    this.dragState.type = 'resize-left';
                } else if (target.classList.contains('resize-handle-right')) {
                    this.dragState.type = 'resize-right';
                } else {
                    this.dragState.type = 'move';
                    clipEl.classList.add('dragging');
                }
            } else if (target.closest('.timeline-track')) {
                // --- NEW: Handle click on empty track area to deselect ---
                // We don't set drag state, this will be handled on mouseUp.
            }
        };

        const onMouseMove = (e) => {
            if (!this.dragState.active) return;
            e.preventDefault();

            const deltaX = e.clientX - this.dragState.initialX;
            const { type, clipElement, initialLeft, initialWidth } = this.dragState;

            if (type === 'playhead') {
                this.dragState.hasMoved = true;
                const rulerContainer = this.container.querySelector('.timeline-ruler-container');
                const rect = rulerContainer.getBoundingClientRect();
                const x = e.clientX - rect.left + this.scrollContainer.scrollLeft;
                const time = Math.max(0, x / this.pixelsPerSecond);
                this.emit('seek', Math.min(time, this.totalDuration));
            } else if (type === 'move') {
                this.dragState.hasMoved = true;
                const newLeft = Math.max(0, initialLeft + deltaX);
                clipElement.style.left = `${newLeft}px`;
            } else if (type === 'resize-right') {
                this.dragState.hasMoved = true;
                const newWidth = Math.max(20, initialWidth + deltaX);
                clipElement.style.width = `${newWidth}px`;
            } else if (type === 'resize-left') {
                this.dragState.hasMoved = true;
                const newLeft = Math.max(0, initialLeft + deltaX);
                const newWidth = Math.max(20, initialWidth - deltaX);
                clipElement.style.left = `${newLeft}px`;
                clipElement.style.width = `${newWidth}px`;
            }
        };

        const onMouseUp = (e) => {
            if (!this.dragState.active) {
                // --- NEW: Handle simple click on empty track for deselection ---
                const target = e.target;
                if (target.closest('.timeline-track') && !target.closest('.timeline-clip') && this.selectedClipId !== null) {
                    this.emit('clipSelected', null);
                }
                return;
            }

            const { type, clipId, clipElement, hasMoved } = this.dragState;

            if (hasMoved && type !== 'playhead') {
                const updates = [];
                const draggedClip = this.clips.find(c => c._id === clipId);
                if (!draggedClip) return;

                const newStartTime = parseFloat(clipElement.style.left) / this.pixelsPerSecond;
                let newDuration = parseFloat(clipElement.style.width) / this.pixelsPerSecond;

                if (type === 'resize-left') {
                    const originalEndTime = (this.dragState.initialLeft + this.dragState.initialWidth) / this.pixelsPerSecond;
                    newDuration = originalEndTime - newStartTime;
                }

                updates.push({ _id: clipId, startTime: newStartTime, duration: newDuration });

                const shouldRipple = this.isRippleEditEnabled ? !e.altKey : e.altKey;

                if (shouldRipple) {
                    const originalStartTime = this.dragState.initialLeft / this.pixelsPerSecond;
                    const originalDuration = this.dragState.initialWidth / this.pixelsPerSecond;
                    const originalEndTime = originalStartTime + originalDuration;
                    const newEndTime = newStartTime + newDuration;
                    const totalShift = newEndTime - originalEndTime;

                    if (Math.abs(totalShift) > 0.01) {
                        this.clips.forEach(clip => {
                            if (clip._id !== clipId && clip.type === draggedClip.type && clip.startTime >= originalEndTime - 0.01) {
                                updates.push({ _id: clip._id, startTime: clip.startTime + totalShift });
                            }
                        });
                    }
                }

                this.emit('updateClipBatch', { updates });

            } else if (type !== 'playhead') {
                const clickedClip = e.target.closest('.timeline-clip');
                if (clickedClip) {
                    this.emit('clipSelected', clickedClip.dataset.clipId);
                }
            }

            if (clipElement) {
                clipElement.classList.remove('dragging');
            }

            this.dragState = { active: false, type: null, clipId: null, clipElement: null, initialX: 0, initialLeft: 0, initialWidth: 0, hasMoved: false };
        };

        this.container.addEventListener('mousedown', onMouseDown);
        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);

        this.scrollContainer.addEventListener('scroll', () => this.syncScroll());

        this.cleanup = () => {
            this.container.removeEventListener('mousedown', onMouseDown);
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
            this.scrollContainer.removeEventListener('scroll', this.syncScroll);
        };
    }

    updateRippleButton() {
        if (!this.rippleEditToggleBtn) return;
        const icon = this.rippleEditToggleBtn.querySelector('i');
        if (this.isRippleEditEnabled) {
            this.rippleEditToggleBtn.classList.add('active', 'btn-primary');
            this.rippleEditToggleBtn.classList.remove('btn-outline-secondary');
            if (icon) icon.className = 'bi bi-link-45deg';
            this.rippleEditToggleBtn.title = 'Ripple Edit is ON (Hold Alt to override)';
        } else {
            this.rippleEditToggleBtn.classList.remove('active', 'btn-primary');
            this.rippleEditToggleBtn.classList.add('btn-outline-secondary');
            if (icon) icon.className = 'bi bi-unlink';
            this.rippleEditToggleBtn.title = 'Ripple Edit is OFF (Hold Alt to override)';
        }
    }



    clipsOverlap(clip1, clip2) {
        const clip1End = clip1.startTime + clip1.duration;
        const clip2End = clip2.startTime + clip2.duration;
        return !(clip1End <= clip2.startTime || clip2End <= clip1.startTime);
    }

    async resizeClipToAudioLength(clip) {
        try {
            const audio = new Audio(`/${clip.assetPath}`);
            await new Promise((resolve, reject) => {
                audio.addEventListener('loadedmetadata', resolve);
                audio.addEventListener('error', reject);
            });
            const audioDuration = audio.duration;
            if (audioDuration > 0) {
                this.emit('updateClipBatch', {
                    updates: [{ _id: clip._id, duration: audioDuration }]
                });
            }
        } catch (error) {
            this.log.error('Failed to get audio duration:', error);
        }
    }

    reorganizeTrack() {
        this.emit('reorganizeProjectTracks');
    }

    showDropdownMenu() {
        if (this.dropdownMenu) {
            this.dropdownMenu.classList.add('show');
            this.renderDropdownMenu();
        }
    }

    hideDropdownMenu() {
        if (this.dropdownMenu) {
            this.dropdownMenu.classList.remove('show');
        }
    }

    toggleDropdownMenu() {
        this.dropdownMenu.classList.toggle('show');
    }

    syncScroll() {
        if (this.ruler && this.scrollContainer) {
            this.ruler.style.transform = `translateX(-${this.scrollContainer.scrollLeft}px)`;
        }
    }

    togglePlayPause() {
        this.isPlaying = !this.isPlaying;
        this.emit(this.isPlaying ? 'play' : 'pause');
        this.updatePlayPauseButton();
    }

    updatePlayPauseButton() {
        if (this.playPauseBtn) {
            const icon = this.playPauseBtn.querySelector('i');
            if (icon) icon.className = `bi ${this.isPlaying ? 'bi-pause-fill' : 'bi-play-fill'}`;
        }
    }

    setPlayingState(isPlaying) {
        if (this.isPlaying !== isPlaying) {
            this.isPlaying = isPlaying;
            this.updatePlayPauseButton();
        }
    }

    updatePlayhead(currentTime) {
        this.currentTime = currentTime;
        this.updateTimeDisplay();

        if (!this.playhead || !this.scrollContainer) return;

        const newLeft = currentTime * this.pixelsPerSecond;
        this.playhead.style.left = `${newLeft}px`;

        // --- START OF FIX: Smart Playhead Scrolling Logic ---
        // Only auto-scroll if the timeline is playing.
        if (this.isPlaying) {
            const scrollLeft = this.scrollContainer.scrollLeft;
            const clientWidth = this.scrollContainer.clientWidth;
            const scrollRight = scrollLeft + clientWidth;

            // Define a "safe zone" near the edges of the viewport.
            const buffer = clientWidth * 0.2; // 20% buffer on each side

            // Check if the playhead is currently outside the visible area.
            const isPlayheadVisible = newLeft >= scrollLeft && newLeft <= scrollRight;

            // If the playhead is visible, we can consider auto-scrolling.
            if (isPlayheadVisible) {
                // If playhead enters the right-side buffer zone, scroll forward.
                if (newLeft > scrollRight - buffer) {
                    this.scrollContainer.scrollLeft += newLeft - (scrollRight - buffer);
                }
                // If playhead enters the left-side buffer zone, scroll backward.
                else if (newLeft < scrollLeft + buffer) {
                    this.scrollContainer.scrollLeft -= (scrollLeft + buffer) - newLeft;
                }
            }
        }
        // --- END OF FIX ---
    }


    updateTimeDisplay() {
        if (this.currentTimeEl) this.currentTimeEl.textContent = this.formatTime(this.currentTime);
        if (this.totalDurationEl) this.totalDurationEl.textContent = this.formatTime(this.totalDuration);
    }

    formatTime(seconds) {
        const min = Math.floor(seconds / 60);
        const sec = Math.floor(seconds % 60);
        const ms = Math.floor((seconds % 1) * 100);
        return `${String(min).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
    }

    destroy() {
        if (this.cleanup) this.cleanup();
        super.destroy();
    }
}